import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Badge,
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    CustomInput,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Select from "react-select";
import Swal from "sweetalert2";
import { Bar } from "react-chartjs-2";
import {
    getRoomDetail,
    updateRoomMetadata,
    archiveRoom,
    getAllCountries,
    getAllCities,
    getAllRoomTypes,
    getAllModerators,
    assignModerator
} from "../../api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – High Fidelity & Premium UI
   ═══════════════════════════════════════════════════ */
const styles = {
    darkHeader: {
        background: "linear-gradient(135deg, #172b4d 0%, #212529 100%)",
        borderRadius: "16px",
        padding: "35px 40px",
        color: "#fff",
        marginBottom: "30px",
        position: "relative",
        boxShadow: "0 15px 35px rgba(23,43,77,0.2)",
        overflow: "hidden",
        minHeight: "200px"
    },
    backBtn: {
        width: "36px",
        height: "36px",
        borderRadius: "10px",
        background: "rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        cursor: "pointer",
        transition: "all 0.2s",
        border: "none",
        marginRight: "20px"
    },
    avatarBig: {
        width: "60px",
        height: "60px",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #5e72e4, #825ee4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "24px",
        fontWeight: 700,
        marginRight: "20px",
        boxShadow: "0 8px 16px rgba(0,0,0,0.2)"
    },
    metricCard: {
        background: "#fff",
        borderRadius: "12px",
        padding: "24px 15px",
        border: "1px solid #e9ecef",
        textAlign: "center",
        height: "100%",
        boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
    },
    metricValue: {
        fontSize: "32px",
        fontWeight: 800,
        marginBottom: "2px"
    },
    metricLabel: {
        fontSize: "13px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        color: "#525f7f"
    },
    metaTable: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: "0 12px"
    },
    metaLabel: {
        width: "35%",
        fontSize: "15px",
        color: "#32325d",
        fontWeight: 600,
        padding: "8px 0"
    },
    metaValue: {
        fontSize: "16px",
        color: "#172b4d",
        fontWeight: 700,
        textAlign: "right",
        padding: "8px 0"
    }
};

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: "8px",
        minHeight: "45px",
        border: "1px solid #dee2e6",
        boxShadow: state.isFocused ? "0 0 0 1px #5e72e4" : "none",
        "&:hover": { borderColor: "#5e72e4" },
        fontSize: "16px",
        background: "#fff",
        color: "#525f7f"
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f8f9fe" : "#fff",
        color: state.isSelected ? "#fff" : "#525f7f",
        fontSize: "16px",
        padding: "10px 15px",
        cursor: "pointer"
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        border: "none",
        marginTop: "8px",
        overflow: "hidden",
        zIndex: 9999
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (provided) => ({ ...provided, color: "#adb5bd" }),
    singleValue: (provided) => ({ ...provided, color: "#525f7f" })
};

const modalSelectStyles = {
    control: (provided) => ({
        ...provided,
        borderRadius: "8px",
        minHeight: "45px",
        border: "1px solid #dee2e6",
        boxShadow: "none",
        "&:hover": { borderColor: "#5e72e4" },
        fontSize: "15px",
        background: "#fff",
        color: "#525f7f"
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f8f9fe" : "#fff",
        color: state.isSelected ? "#fff" : "#525f7f",
        fontSize: "15px",
        padding: "10px 15px",
        cursor: "pointer"
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        border: "none",
        marginTop: "8px",
        overflow: "hidden",
        zIndex: 9999
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (provided) => ({ ...provided, color: "#adb5bd" }),
    singleValue: (provided) => ({ ...provided, color: "#172b4d" })
};

const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "k";
    return num.toString();
};

const RoomDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edit Modal State
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [errors, setErrors] = useState({});

    // Moderator State
    const [moderatorModalOpen, setModeratorModalOpen] = useState(false);
    const [allAdmins, setAllAdmins] = useState([]);
    const [selectedModerator, setSelectedModerator] = useState("");

    const fetchDetail = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getRoomDetail(id);
            if (res.status === true || res.status === "success") {
                setRoom(res.payload);
                setFormData({
                    r_name: res.payload.r_name || "",
                    r_type: res.payload.r_type?.rt_uuid || res.payload.r_type || res.payload.type || "",
                    r_country: res.payload.r_country?.c_uuid || res.payload.r_country || res.payload.country || "",
                    r_city: res.payload.r_city?.ci_uuid || res.payload.r_city || res.payload.city || "",
                    r_visibility: res.payload.r_visibility || "Public",
                    r_eligible: res.payload.r_eligible !== undefined ? res.payload.r_eligible : true,
                    r_tags: res.payload.r_tags?.join(", ") || ""
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    useEffect(() => {
        if (editModalOpen) {
            getAllCountries(1, 1000, "", "1").then(res => setCountries(res.payload?.countries || res.payload || []));
            getAllRoomTypes(1, 1000, "", "1").then(res => setRoomTypes(res.payload?.roomTypes || res.payload || []));
        }
    }, [editModalOpen]);

    useEffect(() => {
        if (formData.r_country) {
            const matchedCountry = countries.find(c => c.c_uuid === formData.r_country || c.c_name === formData.r_country || c._id === formData.r_country);
            const countryId = matchedCountry ? matchedCountry.c_uuid : formData.r_country;
            getAllCities(1, 1000, countryId, "").then(res => setCities(res.payload?.cities || []));
        }
    }, [formData.r_country, countries]);

    useEffect(() => {
        if (moderatorModalOpen) {
            getAllModerators().then(res => {
                if (res.status === true || res.status === "success") {
                    setAllAdmins(res.payload || []);
                    console.log("allAdmins", res.payload);
                }
            });
        }
    }, [moderatorModalOpen]);

    const metrics = room?.metrics || {};

    // Process Real Weekly Activity (Posts & Active Users)
    const weeklyRaw = metrics.weeklyActivity || [];
    const weeklyUsersRaw = metrics.weeklyActiveUsers || [];
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const last7DaysLabels = [];
    const last7DaysPosts = [];
    const last7DaysUsers = [];

    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const label = days[d.getDay()];
        const dateStr = d.toISOString().split("T")[0];

        const msgMatch = weeklyRaw.find(w => w._id === dateStr);
        const userMatch = weeklyUsersRaw.find(w => w._id === dateStr);

        last7DaysLabels.push(label);
        last7DaysPosts.push(msgMatch ? msgMatch.count : 0);
        last7DaysUsers.push(userMatch ? userMatch.count : 0);
    }

    const chartData = {
        labels: last7DaysLabels,
        datasets: [
            {
                label: "Posts",
                data: last7DaysPosts,
                backgroundColor: "rgba(94, 114, 228, 0.8)",
                borderRadius: 4,
                barThickness: 15
            },
            // {
            //     label: "Active Users",
            //     data: last7DaysUsers,
            //     backgroundColor: "rgba(45, 206, 137, 0.8)",
            //     borderRadius: 4,
            //     barThickness: 15
            // }
        ]
    };

    const chartOptions = {
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: "bottom",
            labels: { boxWidth: 10, fontSize: 10, fontColor: "#8898aa", padding: 20 }
        },
        scales: {
            yAxes: [{
                ticks: { beginAtZero: true, fontSize: 10, fontColor: "#adb5bd" },
                gridLines: { color: "rgba(0,0,0,0.05)", zeroLineColor: "transparent" }
            }],
            xAxes: [{
                gridLines: { display: false },
                ticks: { fontSize: 11, fontColor: "#adb5bd", fontWeight: 600 }
            }]
        },
        tooltips: {
            mode: "index",
            intersect: false,
            backgroundColor: "#172b4d",
            titleFontSize: 12,
            bodyFontSize: 12,
            cornerRadius: 4,
            padding: 10
        }
    };

    const handleArchive = async () => {
        if (!room) return;
        const isArchived = room.r_status === "Archived";
        const res = await Swal.fire({
            title: isArchived ? "Restore Room?" : "Archive Room?",
            text: `Are you sure you want to ${isArchived ? "restore" : "archive"} this room?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: isArchived ? "#2dce89" : "#f5365c"
        });

        if (res.isConfirmed) {
            try {
                await archiveRoom(id, isArchived ? "Active" : "Archived");
                Swal.fire("Success", `Room ${isArchived ? "Restored" : "Archived"}`, "success");
                fetchDetail();
            } catch (err) {
                Swal.fire("Error", "Action failed", "error");
            }
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.r_name?.trim()) newErrors.r_name = "Room Name is required";
        if (!formData.r_type) newErrors.r_type = "Room Type is required";
        if (!formData.r_country) newErrors.r_country = "Country is required";
        if (!formData.r_city) newErrors.r_city = "City is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        try {
            const payload = {
                ...formData,
                r_tags: formData.r_tags.split(",").map(t => t.trim()).filter(t => t)
            };
            await updateRoomMetadata(id, payload);
            Swal.fire("Success", "Room updated", "success");
            setEditModalOpen(false);
            fetchDetail();
        } catch (err) {
            Swal.fire("Error", "Update failed", "error");
        }
    };

    const handleModeratorSubmit = async (e) => {
        e.preventDefault();
        if (!selectedModerator) return;
        try {
            const res = await assignModerator(id, { userId: selectedModerator });
            if (res.status === true || res.status === "success") {
                Swal.fire("Success", "Moderator assigned successfully", "success");
                setModeratorModalOpen(false);
                setSelectedModerator("");
                fetchDetail();
            } else {
                Swal.fire("Error", res.message || "Assignment failed", "error");
            }
        } catch (err) {
            Swal.fire("Error", "Assignment failed", "error");
        }
    };

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                {/* Premium Dark Header - Shell remains while loading */}
                <div style={styles.darkHeader}>
                    {room ? (
                        <Row className="align-items-center">
                            <Col className="d-flex align-items-center">
                                <button style={styles.backBtn} onClick={() => navigate("/admin/rooms-chats")}>
                                    <i className="fas fa-arrow-left" />
                                </button>
                                <div style={styles.avatarBig}>{room.r_name.charAt(0)}</div>
                                <div>
                                    <div className="d-flex align-items-center mb-1">
                                        <h2 className="text-white mb-0 mr-3" style={{ fontWeight: 800 }}>{room.r_name}</h2>
                                        <Badge color="success" pill style={{ background: "rgba(45,206,137,0.2)", color: "#2dce89", fontSize: "10px", padding: "5px 12px" }}>
                                            <i className="fas fa-circle mr-2" style={{ fontSize: "6px" }} /> {room.r_status?.toUpperCase() || "ACTIVE"}
                                        </Badge>
                                    </div>
                                    <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>
                                        • {room.type || room.r_type} • {room.country}, {room.city} • Created {new Date(room.createdAt).toLocaleDateString()}
                                    </div>
                                    <p className="mt-3 mb-0" style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", maxWidth: "600px" }}>
                                        {room.r_description || "A vibrant community chatroom for connection and engagement."}
                                    </p>
                                </div>
                            </Col>
                            <Col xs="auto" className="text-right">
                                <Button size="sm" color="info" className="mr-2" style={{ borderRadius: "8px", padding: "8px 20px" }} onClick={() => setModeratorModalOpen(true)}>
                                    <i className="fas fa-user-shield mr-2" /> Assign Moderator
                                </Button>
                                <Button size="sm" color="primary" className="ml-2" style={{ borderRadius: "8px", padding: "8px 20px" }} onClick={() => setEditModalOpen(true)}>
                                    <i className="fas fa-edit mr-2" /> Edit
                                </Button>
                                <Button size="sm" color="danger" className="ml-2" style={{ borderRadius: "8px", padding: "8px 20px" }} onClick={handleArchive}>
                                    <i className="fas fa-archive mr-2" /> {room.r_status === "Archived" ? "Restore" : "Archive"}
                                </Button>
                            </Col>
                        </Row>
                    ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 py-5">
                            <i className="fas fa-circle-notch fa-spin fa-2x text-white opacity-5" />
                        </div>
                    )}
                </div>

                {/* Metric Overviews */}
                <Row className="mb-4">
                    <Col lg="15" className="d-flex" style={{ gap: "15px", flexWrap: "wrap" }}>
                        {/* <div style={{ flex: "1 1 0", minWidth: "160px" }}>
                            <div style={styles.metricCard}><div className="mb-2"><i className="fas fa-user-friends text-primary" /></div><div style={{ ...styles.metricValue, color: "#5e72e4" }}>{loading ? "..." : formatNumber(metrics.activeUsersCount)}</div><div style={styles.metricLabel}>Active Users</div></div>
                        </div> */}
                        <div style={{ flex: "1 1 0", minWidth: "160px" }}>
                            <div style={styles.metricCard}><div className="mb-2"><i className="fas fa-users text-info" /></div><div style={{ ...styles.metricValue, color: "#11cdef" }}>{loading ? "..." : formatNumber(room?.r_usersCount)}</div><div style={styles.metricLabel}>Total Users</div></div>
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: "160px" }}>
                            <div style={styles.metricCard}><div className="mb-2"><i className="fas fa-file-alt text-success" /></div><div style={{ ...styles.metricValue, color: "#2dce89" }}>{loading ? "..." : formatNumber(metrics.totalPosts)}</div><div style={styles.metricLabel}>Total Posts</div></div>
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: "160px" }}>
                            <div style={styles.metricCard}><div className="mb-2"><i className="fas fa-lightbulb text-warning" /></div><div style={{ ...styles.metricValue, color: "#fb6340" }}>{loading ? "..." : formatNumber(metrics.totalInnovations)}</div><div style={styles.metricLabel}>Innovation Posted</div></div>
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: "160px" }}>
                            <div style={styles.metricCard}><div className="mb-2"><i className="fas fa-hand-holding-usd text-success" /></div><div style={{ ...styles.metricValue, color: "#2dce89" }}>{loading ? "..." : `$${formatNumber(metrics.totalFundRaised)}`}</div><div style={styles.metricLabel}>Total Fund Raised</div></div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    {/* Metadata Section */}
                    <Col lg="7">
                        <Card className="shadow-sm border-0" style={{ borderRadius: "16px" }}>
                            <CardBody className="p-4">
                                <h6 style={{ fontSize: "13px", fontWeight: 800, color: "#32325d", display: "flex", alignItems: "center" }} className="mb-4">
                                    <i className="fas fa-info-circle mr-2 text-primary" /> ROOM METADATA
                                </h6>
                                <table style={styles.metaTable}>
                                    <tbody>
                                        <tr>
                                            <td style={styles.metaLabel}>Type</td>
                                            <td style={styles.metaValue}>{room ? <Badge color="success" style={{ background: "#e1f7ec", color: "#2dce89", fontSize: "12px", fontWeight: 700, padding: "6px 14px" }}>{(room.type || room.r_type)?.toUpperCase()}</Badge> : "..."}</td>
                                        </tr>
                                        <tr><td style={styles.metaLabel}>Country</td><td style={styles.metaValue}>{loading ? "..." : room?.country}</td></tr>
                                        <tr><td style={styles.metaLabel}>City</td><td style={styles.metaValue}>{loading ? "..." : room?.city}</td></tr>
                                        <tr><td style={styles.metaLabel}>Creator</td><td style={styles.metaValue}>{loading ? "..." : (room?.userProfile?.name || "System")}</td></tr>
                                        {/* <tr>
                                            <td style={styles.metaLabel}>Visibility</td>
                                            <td style={styles.metaValue}>
                                                {room ? (
                                                    <Badge color="info" pill style={{ background: "#e1f5fe", color: "#03a9f4", fontSize: "10px", padding: "4px 10px" }}>
                                                        <i className="fas fa-globe mr-1" /> {room.r_visibility?.toUpperCase()}
                                                    </Badge>
                                                ) : "..."}
                                            </td>
                                        </tr> */}
                                        <tr>
                                            <td style={styles.metaLabel}>Reputation Eligibility</td>
                                            <td style={styles.metaValue}>
                                                {room ? (
                                                    <Badge color={room.r_eligible ? "success" : "warning"} pill style={{ background: room.r_eligible ? "#e1f7ec" : "#fff4e5", color: room.r_eligible ? "#2dce89" : "#ff9800", fontSize: "10px", padding: "4px 10px" }}>
                                                        {room.r_eligible ? "✓ ELIGIBLE" : "✗ NOT ELIGIBLE"}
                                                    </Badge>
                                                ) : "..."}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.metaLabel}>Tags</td>

                                            <td
                                                style={{
                                                    ...styles.metaValue,
                                                    verticalAlign: "top",
                                                    textAlign: "right",
                                                }}
                                            >
                                                <div
                                                    className="d-flex flex-wrap justify-content-end align-items-start"
                                                    style={{
                                                        gap: "8px",
                                                        maxWidth: "320px",
                                                        marginLeft: "auto",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {room?.r_tags?.map((t) => (
                                                        <Badge
                                                            key={t}
                                                            pill
                                                            style={{
                                                                background: "#f1f3f9",
                                                                color: "#5e72e4",
                                                                fontSize: "13px",
                                                                fontWeight: "600",
                                                                border: "none",
                                                                whiteSpace: "normal",
                                                                textAlign: "center",
                                                                padding: "8px 14px",
                                                                lineHeight: "1.5",
                                                            }}
                                                        >
                                                            {t}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                        {metrics?.trendingTopics && metrics.trendingTopics.length > 0 && (
                                            <tr>
                                                <td style={styles.metaLabel}>Trending Topics</td>
                                                <td style={{ ...styles.metaValue, verticalAlign: "top", textAlign: "right" }}>
                                                    <div className="d-flex flex-wrap justify-content-end align-items-start" style={{ gap: "6px", maxWidth: "320px", marginLeft: "auto" }}>
                                                        {metrics.trendingTopics.map((topic) => (
                                                            <Badge key={topic} pill style={{ background: "rgba(94, 114, 228, 0.1)", color: "#5e72e4", fontSize: "12px", fontWeight: "600", padding: "6px 12px", border: "1px solid rgba(94, 114, 228, 0.2)" }}>
                                                                #{topic}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Chart Section */}
                    <Col lg="5">
                        <Card className="shadow-sm border-0" style={{ borderRadius: "16px", height: "100%" }}>
                            <CardBody className="p-4 d-flex flex-column">
                                <h6 style={{ fontSize: "13px", fontWeight: 800, color: "#32325d" }} className="mb-4 text-uppercase">
                                    <i className="fas fa-chart-bar mr-2 text-info" /> Weekly Activity
                                </h6>
                                <div style={{ flex: 1, position: "relative", minHeight: "250px" }}>
                                    {loading ? (
                                        <div className="d-flex align-items-center justify-content-center h-100">
                                            <i className="fas fa-spinner fa-spin text-light" />
                                        </div>
                                    ) : (
                                        <Bar data={chartData} options={chartOptions} />
                                    )}
                                </div>
                                <div className="mt-3 d-flex justify-content-between">
                                    <small className="text-muted font-weight-bold" style={{ fontSize: "10px" }}>POSTS (7D): {last7DaysPosts.reduce((a, b) => a + b, 0)}</small>
                                    {/* <small className="text-muted font-weight-bold" style={{ fontSize: "10px" }}>ACTIVE USERS (7D): {metrics.activeUsersCount}</small> */}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Edit Modal */}
            <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} centered size="lg">
                <ModalHeader className="border-bottom-0 pb-0">
                    <h3 className="mb-0 font-weight-bold" style={{ fontSize: "20px" }}>Update Room Settings</h3>
                </ModalHeader>
                <Form onSubmit={handleUpdate}>
                    <ModalBody className="p-4">
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label style={{ fontSize: "15px", fontWeight: 600, color: "#525f7f" }}>Room Name <span className="text-danger">*</span></Label>
                                    <Input
                                        placeholder="Enter Room Name"
                                        style={{ fontSize: "16px", height: "auto", padding: "12px 15px", color: "#172b4d" }}
                                        value={formData.r_name || ""}
                                        onChange={e => {
                                            setFormData({ ...formData, r_name: e.target.value });
                                            if (errors.r_name) setErrors({ ...errors, r_name: null });
                                        }}
                                        invalid={!!errors.r_name}
                                        required
                                    />
                                    {errors.r_name && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.r_name}</div>}
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label style={{ fontSize: "15px", fontWeight: 600, color: "#525f7f" }}>Type <span className="text-danger">*</span></Label>
                                    <Select
                                        styles={modalSelectStyles}
                                        options={roomTypes.map(rt => ({ value: rt.rt_uuid, label: rt.rt_name }))}
                                        value={(() => {
                                            const match = roomTypes.find(rt => rt.rt_uuid === formData.r_type || rt.rt_name === formData.r_type || rt._id === formData.r_type);
                                            if (match) return { value: match.rt_uuid, label: match.rt_name };
                                            if (formData.r_type && !/^[0-9a-fA-F-]{20,}$/.test(formData.r_type)) return { value: formData.r_type, label: formData.r_type };
                                            return null;
                                        })()}
                                        onChange={(opt) => {
                                            setFormData({ ...formData, r_type: opt ? opt.value : "" });
                                            if (errors.r_type) setErrors({ ...errors, r_type: null });
                                        }}
                                        placeholder="Select Type"
                                        isSearchable={true}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    {errors.r_type && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.r_type}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label style={{ fontSize: "15px", fontWeight: 600, color: "#525f7f" }}>Country <span className="text-danger">*</span></Label>
                                    <Select
                                        styles={modalSelectStyles}
                                        options={countries.map(c => ({ value: c.c_uuid, label: c.c_name }))}
                                        value={(() => {
                                            const match = countries.find(c => c.c_uuid === formData.r_country || c.c_name === formData.r_country || c._id === formData.r_country);
                                            if (match) return { value: match.c_uuid, label: match.c_name };
                                            if (formData.r_country && !/^[0-9a-fA-F-]{20,}$/.test(formData.r_country)) return { value: formData.r_country, label: formData.r_country };
                                            return null;
                                        })()}
                                        onChange={(opt) => {
                                            setFormData({ ...formData, r_country: opt ? opt.value : "", r_city: "" });
                                            if (errors.r_country) setErrors({ ...errors, r_country: null });
                                        }}
                                        placeholder="Select Country"
                                        isSearchable={true}
                                        isClearable={true}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    {errors.r_country && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.r_country}</div>}
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label style={{ fontSize: "15px", fontWeight: 600, color: "#525f7f" }}>City <span className="text-danger">*</span></Label>
                                    <Select
                                        styles={modalSelectStyles}
                                        options={cities.map(ci => ({ value: ci.ci_uuid, label: ci.ci_name }))}
                                        value={(() => {
                                            const match = cities.find(ci => ci.ci_uuid === formData.r_city || ci.ci_name === formData.r_city || ci._id === formData.r_city);
                                            if (match) return { value: match.ci_uuid, label: match.ci_name };
                                            if (formData.r_city && !/^[0-9a-fA-F-]{20,}$/.test(formData.r_city)) return { value: formData.r_city, label: formData.r_city };
                                            return null;
                                        })()}
                                        onChange={(opt) => {
                                            setFormData({ ...formData, r_city: opt ? opt.value : "" });
                                            if (errors.r_city) setErrors({ ...errors, r_city: null });
                                        }}
                                        placeholder="Select City"
                                        isSearchable={true}
                                        isClearable={true}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    {errors.r_city && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.r_city}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            {/* <Col md="6">
                                <FormGroup>
                                    <Label style={{ fontSize: "15px", fontWeight: 600, color: "#525f7f" }}>Visibility</Label>
                                    <Input
                                        type="select"
                                        style={{ fontSize: "16px", height: "auto", padding: "12px 15px" }}
                                        value={formData.r_visibility}
                                        onChange={e => setFormData({ ...formData, r_visibility: e.target.value })}
                                    >
                                        <option>Public</option><option>Private</option>
                                    </Input>
                                </FormGroup>
                            </Col> */}
                            <Col md="6" className="mt-4 pt-2">
                                <CustomInput
                                    type="switch"
                                    id="eligSwitch"
                                    label={<span style={{ fontSize: "15px", fontWeight: 600, color: "#525f7f" }}>Reputation Eligible</span>}
                                    checked={formData.r_eligible}
                                    onChange={e => setFormData({ ...formData, r_eligible: e.target.checked })}
                                />
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter className="border-top-0 pt-0">
                        <Button color="secondary" style={{ borderRadius: "10px", padding: "10px 25px" }} onClick={() => setEditModalOpen(false)}>Cancel</Button>
                        <Button color="primary" type="submit" style={{ borderRadius: "10px", padding: "10px 25px" }}>Save Changes</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Moderator Modal */}
            <Modal isOpen={moderatorModalOpen} toggle={() => setModeratorModalOpen(false)} centered>
                <ModalHeader>Assign Moderator</ModalHeader>
                <Form onSubmit={handleModeratorSubmit}>
                    <ModalBody className="p-4">
                        <FormGroup>
                            <Label>Select Moderator</Label>
                            <Select
                                value={allAdmins.find(a => String(a._id) === selectedModerator) ? { value: selectedModerator, label: `${allAdmins.find(a => String(a._id) === selectedModerator).au_name || "N/A"} ${allAdmins.find(a => String(a._id) === selectedModerator).au_surname || ""} (${allAdmins.find(a => String(a._id) === selectedModerator).au_email || ""})` } : null}
                                onChange={(opt) => setSelectedModerator(opt ? opt.value : "")}
                                options={allAdmins.map(a => ({ value: String(a._id), label: `${a.au_name || "N/A"} ${a.au_surname || ""} (${a.au_email || ""})` }))}
                                placeholder="Search by name or email..."
                                isSearchable={true}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                isClearable={true}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModeratorModalOpen(false)}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={!selectedModerator}>Assign</Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </>
    );
};

export default RoomDetail;
